/**
 * Fachada entre el store y los componentes.
 *
 * El store guarda lo mínimo; todo lo demás se deriva aquí en cada render. Es a
 * propósito: un dato derivado que se guarda es un dato que puede quedarse
 * desfasado, y en un tablero donde cada revelación mueve el poder de media
 * docena de cartas eso se nota enseguida.
 *
 * Los campos se seleccionan de uno en uno porque Zustand compara por identidad:
 * devolver un objeto nuevo en el selector repintaría en cada cambio del store.
 */

import { useMemo } from 'react';
import { getCardMaster } from '@/data';
import { suggestMove } from '@/engine/aiOpponent';
import { getZoneScores, whyCannotPlay } from '@/engine/combatEngine';
import { isZoneLockedOnTurn, occupancyOf, zoneCapacity } from '@/engine/effects';
import { useGameStore } from '@/store/useGameStore';
import type { ZoneIndex } from '@/types/game';

export function useGameState() {
  const game = useGameStore((state) => state.game);
  const selectedCardId = useGameStore((state) => state.selectedCardId);
  const draggingCardId = useGameStore((state) => state.draggingCardId);
  const notice = useGameStore((state) => state.notice);
  const isResolving = useGameStore((state) => state.isResolving);
  const revealSteps = useGameStore((state) => state.revealSteps);
  const revealIndex = useGameStore((state) => state.revealIndex);
  const inspectedCardId = useGameStore((state) => state.inspectedCardId);
  const hintVisible = useGameStore((state) => state.hintVisible);
  const guideDismissed = useGameStore((state) => state.guideDismissed);

  const newMatch = useGameStore((state) => state.newMatch);
  const selectCard = useGameStore((state) => state.selectCard);
  const setDragging = useGameStore((state) => state.setDragging);
  const playSelectedInto = useGameStore((state) => state.playSelectedInto);
  const playCardInto = useGameStore((state) => state.playCardInto);
  const recall = useGameStore((state) => state.recall);
  const endTurn = useGameStore((state) => state.endTurn);
  const inspectCard = useGameStore((state) => state.inspectCard);
  const toggleHint = useGameStore((state) => state.toggleHint);
  const dismissGuide = useGameStore((state) => state.dismissGuide);

  const zoneScores = useMemo(() => (game ? getZoneScores(game) : []), [game]);

  /** Carta que está esperando destino, sea por selección o por arrastre. */
  const pendingCardId = draggingCardId ?? selectedCardId;

  const currentStep = revealIndex >= 0 ? revealSteps[revealIndex] : undefined;

  /**
   * Zonas donde la carta pendiente se puede soltar de verdad.
   *
   * Se calcula aquí y no en el momento de soltar para poder resaltar los
   * destinos válidos *antes* de que el jugador se comprometa. El motor vuelve a
   * validar de todas formas: esto es ayuda visual, no la regla.
   */
  const validZones = useMemo(() => {
    const result: Record<ZoneIndex, boolean> = { 0: false, 1: false, 2: false };
    if (!game || !pendingCardId || game.phase !== 'PLANNING') return result;

    const player = game.players.PLAYER;
    const card = player.hand.find((item) => item.instanceId === pendingCardId);
    if (!card) return result;

    const cost = getCardMaster(card.masterId).cost;
    if (cost > player.energy) return result;

    for (const zone of game.zones) {
      result[zone.index] =
        !isZoneLockedOnTurn(zone, game.turn) && occupancyOf(zone, 'PLAYER') < zoneCapacity(zone);
    }
    return result;
  }, [game, pendingCardId]);

  /**
   * Motivo por el que la carta elegida no se puede jugar, si lo hay.
   *
   * Se calcula sobre la carta *elegida* y no al intentar jugarla: así el aviso
   * aparece en cuanto se toca la carta, no después de tocar además una zona y
   * que no pase nada.
   */
  const pendingCardIssue = useMemo(() => {
    if (!game || !pendingCardId) return null;
    return whyCannotPlay(game, 'PLAYER', pendingCardId);
  }, [game, pendingCardId]);

  /**
   * Jugada recomendada. Se calcula solo cuando el jugador la pide: recorrer la
   * mano por las tres zonas en cada repintado sería trabajo tirado, y un consejo
   * que aparece sin pedirlo se convierte en ruido.
   */
  const suggestion = useMemo(
    () => (game && hintVisible ? suggestMove(game, 'PLAYER') : null),
    [game, hintVisible],
  );

  return {
    game,
    pendingCardIssue,
    suggestion,
    hintVisible,
    guideDismissed,
    dismissGuide,
    inspectedCardId,
    inspectCard,
    toggleHint,
    player: game?.players.PLAYER ?? null,
    opponent: game?.players.OPPONENT ?? null,
    zoneScores,
    validZones,
    pendingCardId,
    selectedCardId,
    notice,
    isResolving,
    /** Carta que se está destapando en este instante, para resaltarla. */
    highlightedCardId: currentStep?.boardCardId ?? null,
    /** Frase del paso de revelación actual. */
    revealMessage: currentStep?.message ?? null,

    newMatch,
    selectCard,
    setDragging,
    playSelectedInto,
    playCardInto,
    recall,
    endTurn,
  };
}
